import { Paragraph } from 'components/elements/paragraph/Paragraph';
import { sanitizeHtml } from 'utils/sanitize';
import { useSessionContext } from 'utils/context/session-provider';
import { LocalStorageKeys, useLocalStorage } from 'utils/localStorage';
import { TroubleshootingCode } from 'components/elements/troubleshooting-code/troubleshooting-code';
import { StyledButton } from 'components/elements/button/Button.styles';
import { usePolyglotT } from 'utils/locale/locale';
import { DatadogClient } from '@workivate/tho-web-shared';
import styled from 'styled-components';
import { breakpoints } from 'themes/helpers';
import { SvgIcon } from 'components/elements/svg-icon/SvgIcon';

const ButtonHolder = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;

  @media (min-width: ${() => breakpoints.tablet}px) {
    flex-direction: row;
  }

  > * {
    flex: 1;
    margin-top: 0;
  }
`;

const OpenInNewPageIcon = styled(SvgIcon)`
  vertical-align: middle;
  margin-left: 6px;
`;

type TroubleshootingDetailsProps = {
  /**
   * Optional callback for a "Try again" button. When provided, a secondary
   * outline button is shown alongside the "Raise a ticket" button.
   * Use this on login pages where the user can immediately retry.
   * Do NOT pass this on the error page where the user must restart from the originating app.
   */
  tryAgainCallback?: () => void;
};

export const TroubleshootingDetails = ({
  tryAgainCallback,
}: TroubleshootingDetailsProps) => {
  const { t, helpLinks } = usePolyglotT();

  // If possible, display the public client id or correlation id so Support can find the session in logs.
  // Priority:
  // 1. clientId from session context (populated from URL param 'client_id' on page load)
  // 2. clientId from localStorage (written by USL session management)
  // 3. correlationId from DatadogClient (checks URL param 'x-correlation-id', then localStorage 'x-correlation-id')
  //    generateIfMissing:false ensures we never show a spurious newly-generated ID

  const { clientId } = useSessionContext();

  const [storedClientId] = useLocalStorage<string>(LocalStorageKeys.clientId);

  const finalClientId = clientId || storedClientId || undefined;
  const finalCorrelationId =
    DatadogClient.getCorrelationId({ generateIfMissing: false }) || undefined;

  // Use PCI (public client id) for preference, or correlation id if PCI is not available
  const errorId = finalClientId || finalCorrelationId || undefined;

  // The label prop is not translated because it tells Support which code has been provided
  // and matches the field name in the logs
  const errorIdLabel = finalClientId ? 'Public Client ID' : 'Correlation ID';

  const helpCentreUrl = helpLinks.help_centre_account_access;

  const errorIdText = errorId
    ? t('troubleshooting_details.description_with_error_id_raise_ticket')
    : t('troubleshooting_details.description_no_error_id_raise_ticket');

  return (
    <div>
      <Paragraph
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(errorIdText) }}
      />
      {errorId && <TroubleshootingCode code={errorId} label={errorIdLabel} />}
      <ButtonHolder>
        {tryAgainCallback && (
          <StyledButton
            data-hook='try-again'
            variant='outline'
            isFull={true}
            onClick={tryAgainCallback}
          >
            {t('troubleshooting_details.try_again')}
          </StyledButton>
        )}
        {/* rendered as a link so that it is semantically a link, however we want it to look like a button */}
        <StyledButton
          as='a'
          href={helpCentreUrl}
          target='_blank'
          rel='noopener noreferrer'
          variant='primary'
          isFull={true}
          style={{ textDecoration: 'none' }}
        >
          {t('troubleshooting_details.button_raise_ticket')}
          <OpenInNewPageIcon
            src='/icons/open-new-page.svg'
            height={18}
            width={18}
          />
        </StyledButton>
      </ButtonHolder>
    </div>
  );
};
